import { AlertType, Season, TimeSlot } from '@types';
import {
  AlertModal,
  Card,
  EmptyState,
  LoadingSpinner,
  SectionHeader,
} from '_atoms';
import { SeasonDetailsService, TicketService } from '_services';
import HelperMethods from '_utils/HelperMethods';
import React, { useCallback, useEffect, useState } from 'react';
import { DayPicker } from 'react-day-picker';
import {
  MdClose,
  MdEvent,
  MdExpandLess,
  MdExpandMore,
  MdPeople,
  MdSchedule,
  MdTouchApp,
  MdVisibility,
} from 'react-icons/md';

interface ViewTicketAvailabilityProps {
  className?: string;
  isOpen?: boolean;
  onClose?: () => void;
  onSessionSelect?: (
    sessionId: string,
    sessionDate: string,
    sessionTime: string,
  ) => void;
}

export const ViewTicketAvailability: React.FC<ViewTicketAvailabilityProps> = ({
  className = '',
  isOpen: isOpenProp,
  onClose: onCloseProp,
  onSessionSelect,
}) => {
  // Helper function to convert Date to local YYYY-MM-DD format
  const getLocalDateString = (date: Date): string => {
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  // Helper function to parse YYYY-MM-DD string as local date (not UTC)
  const parseLocalDate = (dateStr: string): Date => {
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  const [selectedDate, setSelectedDate] = useState<string>(
    getLocalDateString(new Date()),
  );
  const [settings, setSettings] = useState<Season>({
    id: '',
    startDate: '',
    endDate: '',
    isTicketPurchasesEnabled: false,
    isSeasonPassPurchasesEnabled: false,
    isPartyPackagePurchasesEnabled: false,
    isSkatingLessonPurchasesEnabled: false,
    isHockeyLessonPurchasesEnabled: false,
    sessionTimes: [],
  });
  const [isLoadingTimeSlots, setIsLoadingTimeSlots] = useState(false);
  const [timeSlots, setTimeSlots] = useState<TimeSlot[]>([]);
  const [isLoadingInitial, setIsLoadingInitial] = useState(true);
  const [isCalendarExpanded, setIsCalendarExpanded] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);

  // Use external control if provided, otherwise use internal state
  const isModalOpen = isOpenProp !== undefined ? isOpenProp : isViewOpen;
  const [alertModal, setAlertModal] = useState<{
    isOpen: boolean;
    type: AlertType;
    title: string;
    message: string;
  }>({
    isOpen: false,
    type: 'info',
    title: '',
    message: '',
  });

  const showAlert = useCallback(
    (type: AlertType, title: string, message: string) => {
      setAlertModal({ isOpen: true, type, title, message });
    },
    [],
  );

  // Load current season details on page load
  useEffect(() => {
    const loadCurrentSeason = async () => {
      setIsLoadingInitial(true);
      try {
        const responseData =
          (await SeasonDetailsService.getCurrentSeasonDetails()) as unknown as Season;

        if (
          responseData &&
          (responseData.startDate ||
            responseData.endDate ||
            responseData.sessionTimes.length > 0)
        ) {
          setSettings(responseData);
        } else {
          showAlert(
            'info',
            'Info',
            'There are currently no active season settings.',
          );
        }
      } catch (error) {
        console.error('Error loading current season:', error);
        showAlert('error', 'Error', 'Failed to load current season settings.');
      } finally {
        setIsLoadingInitial(false);
      }
    };

    loadCurrentSeason();
  }, [showAlert]);

  const loadTimeSlotsForDate = useCallback(
    async (date: string) => {
      setIsLoadingTimeSlots(true);
      try {
        const responseData = (await TicketService.getTimeSlotsForDate(
          date,
        )) as unknown as TimeSlot[];

        setTimeSlots(responseData || []);
      } catch (error) {
        console.error('Error loading time slots:', error);
        showAlert(
          'error',
          'Error',
          'Failed to load skating sessions for the selected date.',
        );
        setTimeSlots([]);
      } finally {
        setIsLoadingTimeSlots(false);
      }
    },
    [showAlert],
  );

  useEffect(() => {
    if (selectedDate && isModalOpen) {
      loadTimeSlotsForDate(selectedDate);
    }
  }, [selectedDate, isModalOpen, loadTimeSlotsForDate]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isModalOpen]);

  const closeAlert = () => {
    setAlertModal({ isOpen: false, type: 'info', title: '', message: '' });
  };

  const handleViewAvailability = () => {
    setIsViewOpen(true);
    if (selectedDate) {
      loadTimeSlotsForDate(selectedDate);
    }
  };

  const handleClose = useCallback(() => {
    if (onCloseProp) {
      onCloseProp();
    } else {
      setIsViewOpen(false);
    }
    setIsCalendarExpanded(false);
  }, [onCloseProp]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isModalOpen) {
        handleClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isModalOpen, handleClose]);

  const handleSessionClick = (sessionId: string, sessionTime: string) => {
    if (onSessionSelect && selectedDate) {
      // Format the date as a local date string to avoid timezone issues
      const formattedDate = parseLocalDate(selectedDate).toLocaleDateString(
        'en-US',
        {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        },
      );
      onSessionSelect(sessionId, formattedDate, sessionTime);
    }
  };

  return (
    <>
      {/* View Ticket Availability Button - Only show if not controlled externally */}
      {isOpenProp === undefined && (
        <button
          onClick={handleViewAvailability}
          disabled={isLoadingInitial}
          className={`px-4 sm:px-6 py-2.5 bg-purple-500 text-white font-medium rounded-lg hover:bg-purple-600 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-sm hover:shadow-md flex items-center justify-center gap-2 text-sm sm:text-base ${className}`}
        >
          <MdVisibility className="text-lg sm:text-xl" />
          Ticket Availability
        </button>
      )}

      {/* Modal Overlay */}
      {isModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50"
          onClick={handleClose}
        >
          {/* Modal Content */}
          <div
            className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col"
            onClick={e => e.stopPropagation()}
          >
            {/* Header with Close Button */}
            <div className="flex items-center justify-between border-b px-4 sm:px-6 py-4 bg-gray-50">
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
                <MdVisibility className="text-purple-600" />
                Ticket Availability
              </h3>
              <button
                onClick={handleClose}
                className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-200 rounded-full transition-colors duration-200"
                aria-label="Close modal"
              >
                <MdClose className="text-2xl" />
              </button>
            </div>

            {/* Modal Body - Scrollable */}
            <div className="overflow-y-auto flex-1 p-4 sm:p-6">
              {isLoadingInitial ? (
                <div className="flex items-center justify-center py-8 sm:py-12">
                  <LoadingSpinner />
                </div>
              ) : (
                <div className="space-y-4 sm:space-y-6">
                  {/* Date Picker - Collapsible */}
                  <div className="w-full">
                    {settings && settings.startDate && (
                      <div className="space-y-2">
                        {/* Collapsed Date Bar */}
                        <Card className="w-full">
                          <div
                            className="p-3 sm:p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50 transition-colors duration-200"
                            onClick={() =>
                              setIsCalendarExpanded(!isCalendarExpanded)
                            }
                          >
                            <div className="flex items-center space-x-2 sm:space-x-3">
                              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-100 rounded-full flex items-center justify-center">
                                <MdEvent className="text-blue-600 text-lg sm:text-xl" />
                              </div>
                              <div>
                                <div className="text-xs sm:text-sm text-gray-500">
                                  Viewing Date
                                </div>
                                <div className="text-base sm:text-lg font-semibold text-gray-900">
                                  {selectedDate
                                    ? parseLocalDate(
                                        selectedDate,
                                      ).toLocaleDateString('en-US', {
                                        weekday: 'long',
                                        year: 'numeric',
                                        month: 'long',
                                        day: 'numeric',
                                      })
                                    : 'Select a Date'}
                                </div>
                              </div>
                            </div>
                            <div className="text-gray-400">
                              {isCalendarExpanded ? (
                                <MdExpandLess className="text-xl sm:text-2xl" />
                              ) : (
                                <MdExpandMore className="text-xl sm:text-2xl" />
                              )}
                            </div>
                          </div>
                        </Card>

                        {/* Season Info */}
                        {settings.startDate && settings.endDate && (
                          <p className="text-xs text-center text-gray-500">
                            Season runs from{' '}
                            {new Date(settings.startDate).toLocaleDateString()}{' '}
                            to {new Date(settings.endDate).toLocaleDateString()}
                          </p>
                        )}

                        {/* Expanded Calendar */}
                        {isCalendarExpanded && (
                          <div className="flex justify-center">
                            <Card className="w-full max-w-md">
                              <div className="p-2 sm:p-4">
                                <DayPicker
                                  mode="single"
                                  startMonth={
                                    settings.startDate
                                      ? new Date(settings.startDate)
                                      : new Date()
                                  }
                                  selected={
                                    selectedDate
                                      ? parseLocalDate(selectedDate)
                                      : undefined
                                  }
                                  onSelect={e => {
                                    setSelectedDate(
                                      e ? getLocalDateString(e) : '',
                                    );
                                    setIsCalendarExpanded(false);
                                  }}
                                />
                              </div>
                            </Card>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Available Sessions - Read Only */}
                  <div className="w-full">
                    {!selectedDate ? (
                      <Card className="w-full h-full flex items-center justify-center py-6 sm:py-8">
                        <EmptyState
                          title="No Date Selected"
                          description="Please select a date to view skating sessions."
                          icon={MdEvent}
                        />
                      </Card>
                    ) : (
                      <div className="w-full">
                        <SectionHeader
                          title={
                            onSessionSelect
                              ? 'Available Sessions (Click to Select)'
                              : 'Available Sessions (Read Only)'
                          }
                          icon={MdSchedule}
                        />

                        {isLoadingTimeSlots ? (
                          <div className="flex items-center justify-center py-6 sm:py-8">
                            <LoadingSpinner />
                          </div>
                        ) : timeSlots.length === 0 ? (
                          <EmptyState
                            title="No Skating Sessions Found"
                            description={`No skating sessions found for ${parseLocalDate(selectedDate).toLocaleDateString()}.`}
                            icon={MdSchedule}
                          />
                        ) : (
                          <div className="space-y-2 sm:space-y-3 mt-3 sm:mt-4 w-full">
                            {timeSlots.map((timeSlot: any) => (
                              <div key={timeSlot.id}>
                                <div
                                  className={`flex w-full gap-2 sm:gap-4 justify-between border rounded-lg p-2.5 sm:p-4 bg-gray-50 border-gray-200 ${
                                    onSessionSelect
                                      ? 'cursor-pointer hover:bg-blue-50 hover:border-blue-300 hover:shadow-md transition-all duration-200'
                                      : ''
                                  }`}
                                  onClick={() =>
                                    onSessionSelect &&
                                    handleSessionClick(
                                      timeSlot.id,
                                      timeSlot.timeSlot,
                                    )
                                  }
                                >
                                  <div className="flex items-center">
                                    <div className="w-7 h-7 sm:w-8 sm:h-8 bg-blue-100 mr-2 sm:mr-3 rounded-full flex items-center justify-center">
                                      <MdSchedule className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                                    </div>
                                    <div>
                                      <div className="font-semibold text-gray-900 text-sm sm:text-base flex items-center gap-2">
                                        {HelperMethods.formatTime12Hour(
                                          timeSlot.timeSlot,
                                        )}
                                        {onSessionSelect && (
                                          <MdTouchApp className="text-purple-600 text-base sm:text-lg" />
                                        )}
                                      </div>
                                      <div className="text-xs sm:text-sm text-gray-500">
                                        {onSessionSelect
                                          ? 'Click to Select'
                                          : 'Start Time'}
                                      </div>
                                    </div>
                                  </div>

                                  <div className="text-right">
                                    <div className="flex items-center justify-end space-x-1.5 sm:space-x-2">
                                      <MdPeople className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                                      <div className="flex items-baseline space-x-1">
                                        <span className="font-bold text-blue-600 text-base sm:text-lg">
                                          {timeSlot.maxCapacity}
                                        </span>
                                        <span className="text-gray-400 text-xs sm:text-sm">
                                          /
                                        </span>
                                        <span className="font-bold text-gray-600 text-sm sm:text-base">
                                          {timeSlot.originalMaxCapacity}
                                        </span>
                                      </div>
                                    </div>
                                    <div className="text-xs sm:text-sm text-gray-500">
                                      (
                                      {timeSlot.originalMaxCapacity -
                                        timeSlot.maxCapacity}
                                      ) Tickets Sold
                                    </div>
                                    {timeSlot.maxCapacity === 0 ? (
                                      <div className="text-xs text-red-600 font-bold mt-0.5 sm:mt-1">
                                        SOLD OUT
                                      </div>
                                    ) : (
                                      timeSlot.maxCapacity < 20 && (
                                        <div className="text-xs text-orange-600 font-medium mt-0.5 sm:mt-1">
                                          Limited Availability
                                        </div>
                                      )
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Alert Modal */}
      <AlertModal
        isOpen={alertModal.isOpen}
        onClose={closeAlert}
        type={alertModal.type}
        title={alertModal.title}
        message={alertModal.message}
      />
    </>
  );
};
